import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';

import type { Disposable } from '@/core/Disposable';
import type { FrameSource } from '@/core/FrameSource';
import { GsapFrameSource } from '@/motion/GsapFrameSource';

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

export interface ScrollToOptions {
  /** Durée en secondes (ignorée si le défilement fluide est désactivé). */
  readonly duration?: number;
  readonly offset?: number;
  /** Le visiteur ne peut pas interrompre le trajet (traversée d'un trou noir). */
  readonly lock?: boolean;
  /** Courbe du trajet, de 0 à 1 (par défaut : départ et arrivée très doux). */
  readonly easing?: (t: number) => number;
}

const easeInOutQuart = (t: number): number => (t < 0.5 ? 8 * t ** 4 : 1 - (-2 * t + 2) ** 4 / 2);

/**
 * Chef d'orchestre du mouvement : défilement fluide (Lenis), synchronisé
 * avec ScrollTrigger et le ticker GSAP, qui sert aussi de boucle au WebGL.
 */
export class MotionEngine implements Disposable {
  readonly frames: FrameSource = new GsapFrameSource();

  private readonly lenis: Lenis | null;
  private locked = false;
  /** Fin prévue du défilement piloté en cours (horloge `performance.now`). */
  private guidedUntil = 0;

  private readonly tick = (time: number): void => {
    this.lenis?.raf(time * 1000);
  };

  constructor(options: { readonly smoothScroll: boolean }) {
    this.lenis = options.smoothScroll ? new Lenis({ lerp: 0.085, wheelMultiplier: 0.9 }) : null;
    this.lenis?.on('scroll', () => {
      ScrollTrigger.update();
    });
    gsap.ticker.add(this.tick);
    gsap.ticker.lagSmoothing(0);
  }

  get smooth(): boolean {
    return this.lenis !== null;
  }

  /**
   * Vrai pendant un défilement piloté par le site (lien d'ancre, traversée).
   * Fondé sur la durée prévue : un trajet interrompu ne bloque jamais l'état.
   */
  get guided(): boolean {
    return performance.now() < this.guidedUntil;
  }

  scrollTo(target: HTMLElement | number, options: ScrollToOptions = {}): void {
    const offset = options.offset ?? 0;
    if (this.lenis) {
      const duration = options.duration ?? 1.4;
      this.guidedUntil = performance.now() + duration * 1000 + 150;
      this.lenis.scrollTo(target, {
        offset,
        duration,
        easing: options.easing ?? easeInOutQuart,
        lock: options.lock ?? false,
      });
      return;
    }
    const top =
      typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + offset });
  }

  /** Fige le défilement, par exemple quand le menu plein écran est ouvert. */
  lock(): void {
    this.locked = true;
    this.lenis?.stop();
    document.documentElement.style.overflow = 'hidden';
  }

  unlock(): void {
    this.locked = false;
    document.documentElement.style.overflow = '';
    this.lenis?.start();
  }

  /**
   * Intercepte les liens d'ancre (`href="#..."`) pour un défilement fluide,
   * dont la durée s'adapte à la distance parcourue.
   */
  bindAnchorLinks(root: Document): () => void {
    const onClick = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const link = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href^="#"]');
      const hash = link?.getAttribute('href');
      if (!link || !hash || hash === '#') return;

      const target = hash === '#top' ? 0 : root.getElementById(decodeURIComponent(hash.slice(1)));
      if (target === null) return;

      event.preventDefault();
      if (this.locked) this.unlock();
      const destination =
        typeof target === 'number' ? target : target.getBoundingClientRect().top + window.scrollY;
      const distance = Math.abs(destination - window.scrollY);
      this.scrollTo(target, { duration: Math.min(2.6, 0.9 + distance / 5000) });
      history.pushState(null, '', hash);
    };

    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('click', onClick);
    };
  }

  dispose(): void {
    gsap.ticker.remove(this.tick);
    this.lenis?.destroy();
  }
}
